/**
 * Camera Compatibility Widget JavaScript
 * Version 1.1.0
 * 
 * Handles manufacturer/model selection and result display
 * API-powered version - queries Google Cloud Run service
 * 
 * Features:
 * - localStorage caching with 2-hour TTL
 * - Duplicate initialization and event listener prevention
 * - Real-time API data fetching with graceful fallback
 * - Analytics tracking with Firestore (non-blocking)
 */

class CameraCompatibilityWidget {
  constructor() {
    // Prevent multiple initializations
    if (window.cameraWidgetInitialized) {
      console.warn('Camera widget already initialized');
      return;
    }
    window.cameraWidgetInitialized = true;

    this.manufacturerSelect = document.getElementById('manufacturer-select');
    this.modelSelect = document.getElementById('model-select');
    this.stepManufacturer = document.getElementById('step-manufacturer');
    this.stepModel = document.getElementById('step-model');
    this.resultsContainer = document.getElementById('camera-results');
    this.cameraInfo = document.getElementById('camera-info');
    this.cameraContent = document.getElementById('camera-content');
    this.loadingIndicator = document.getElementById('loading-indicator');
    this.errorMessage = document.getElementById('error-message');
    this.widgetContainer = document.querySelector('.camera-widget__container');
    this.cameraForm = document.getElementById('camera-form');
    this.changeCameraBtn = document.getElementById('change-camera-btn');

    // API configuration - set via window.CAMERA_API_URL or fallback to deployed Cloud Run service
    this.apiUrl = window.CAMERA_API_URL || 'https://fjw-camera-compatibility-37oq4ijoyq-uc.a.run.app';

    // Cache configuration - 2 hours TTL (matches API cache)
    // Increment CACHE_VERSION whenever a deployment changes the data shape or API behaviour.
    // Any localStorage entry from an older version is automatically discarded.
    this.CACHE_VERSION = 'v3';
    this.cacheTTL = 2 * 60 * 60 * 1000; // 2 hours in milliseconds
    this.cachePrefix = `camera_widget_${this.CACHE_VERSION}_`;

    // Data storage
    this.cameraData = {};
    this.camerasByManufacturer = {};
    this.manufacturers = [];

    this.init();
  }

  async init() {
    this.bindEvents();
    await this.loadManufacturers();
    this.resetWidget();
  }

  /**
   * Get cached data from localStorage
   * @param {string} key - Cache key
   * @returns {any|null} Cached data or null if expired/missing
   */
  getCachedData(key) {
    try {
      const cached = localStorage.getItem(this.cachePrefix + key);
      if (!cached) return null;

      const { data, timestamp } = JSON.parse(cached);
      const now = Date.now();

      // Check if cache has expired
      if (now - timestamp > this.cacheTTL) {
        localStorage.removeItem(this.cachePrefix + key);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Cache read error:', error);
      return null;
    }
  }

  /**
   * Store data in localStorage with timestamp
   * @param {string} key - Cache key
   * @param {any} data - Data to cache
   */
  setCachedData(key, data) {
    try {
      const cacheEntry = {
        data: data,
        timestamp: Date.now()
      };
      localStorage.setItem(this.cachePrefix + key, JSON.stringify(cacheEntry));
    } catch (error) {
      console.error('Cache write error:', error);
      // Continue without caching if localStorage is unavailable
    }
  }

  /**
   * Load manufacturers from API or cache
   */
  async loadManufacturers() {
    try {
      this.showLoading();

      // Check cache first
      const cachedManufacturers = this.getCachedData('manufacturers');
      if (cachedManufacturers) {
        console.log('Using cached manufacturers');
        this.manufacturers = cachedManufacturers;
        this.populateManufacturerSelect();
        this.hideLoading();
        return;
      }

      // Fetch from API if not cached
      const response = await fetch(`${this.apiUrl}/api/manufacturers`);

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      this.manufacturers = data.manufacturers || [];

      // Cache the manufacturers list
      this.setCachedData('manufacturers', this.manufacturers);

      // Populate manufacturer dropdown
      this.populateManufacturerSelect();
      this.hideLoading();
    } catch (error) {
      console.error('Error loading manufacturers:', error);
      this.showError();
    }
  }

  /**
   * Populate manufacturer dropdown
   */
  populateManufacturerSelect() {
    this.manufacturerSelect.innerHTML = '<option value="">Choose manufacturer...</option>';

    this.manufacturers.forEach(manufacturer => {
      const option = document.createElement('option');
      option.value = manufacturer;
      option.textContent = manufacturer;
      this.manufacturerSelect.appendChild(option);
    });
  }

  bindEvents() {
    // Prevent duplicate event listeners
    if (this.eventsAlreadyBound) {
      console.log('Events already bound, skipping');
      return;
    }
    this.eventsAlreadyBound = true;

    // Manufacturer selection
    this.manufacturerSelect.addEventListener('change', (e) => {
      this.onManufacturerChange(e.target.value);
    });

    // Model selection
    this.modelSelect.addEventListener('change', (e) => {
      this.onModelChange(e.target.value);
    });

    // Change camera button
    if (this.changeCameraBtn) {
      this.changeCameraBtn.addEventListener('click', (e) => {
        e.preventDefault(); // Prevent any default button behavior
        e.stopPropagation(); // Stop event bubbling
        this.resetWidget(); // Reset form instead of just showing it
      });
    }
  }

  async onManufacturerChange(manufacturer) {
    if (!manufacturer) {
      this.hideStep('model');
      return;
    }

    // INPUT VALIDATION: Verify manufacturer is in our loaded list
    if (!this.manufacturers.includes(manufacturer)) {
      console.error('Invalid manufacturer selection:', manufacturer);
      this.showError();
      return;
    }

    try {
      this.showLoading();

      // Check cache first
      const cacheKey = `models_${manufacturer.toLowerCase()}`;
      const cachedCameras = this.getCachedData(cacheKey);

      let cameras;
      if (cachedCameras) {
        console.log(`Using cached models for ${manufacturer}`);
        cameras = cachedCameras;
      } else {
        // Fetch cameras for this manufacturer from API
        const response = await fetch(`${this.apiUrl}/api/cameras/${encodeURIComponent(manufacturer)}`);

        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }

        const data = await response.json();
        cameras = data.cameras || [];

        // Cache the camera models for this manufacturer
        this.setCachedData(cacheKey, cameras);
      }

      // Store in local cache
      this.camerasByManufacturer[manufacturer.toLowerCase()] = cameras;
      cameras.forEach(camera => {
        this.cameraData[camera.id] = camera;
      });

      // Clear and populate model dropdown
      this.modelSelect.innerHTML = '<option value="">Choose model...</option>';

      cameras.forEach(camera => {
        const option = document.createElement('option');
        option.value = camera.id;
        option.textContent = camera.model;
        this.modelSelect.appendChild(option);
      });

      this.hideLoading();
      this.showStep('model');
    } catch (error) {
      console.error('Error loading cameras for manufacturer:', error);
      this.showError();
    }
  }

  /**
   * Safely create a text node to prevent XSS
   * @param {string} text - Text to sanitize
   * @returns {Text} Safe text node
   */
  createSafeTextNode(text) {
    return document.createTextNode(text || '');
  }

  /**
   * Safely set text content to prevent XSS
   * @param {HTMLElement} element - Element to set text on
   * @param {string} text - Text to set
   */
  setSafeText(element, text) {
    element.textContent = text || '';
  }

  /**
   * Create element safely with text content
   * @param {string} tagName - HTML tag name
   * @param {string} className - CSS class name(s)
   * @param {string} textContent - Safe text content
   * @returns {HTMLElement} Created element
   */
  createSafeElement(tagName, className = '', textContent = '') {
    const element = document.createElement(tagName);
    if (className) element.className = className;
    if (textContent) element.textContent = textContent;
    return element;
  }

  /**
   * Track camera search analytics (async, non-blocking)
   * @param {string} manufacturer - Camera manufacturer
   * @param {string} model - Camera model
   */
  async trackCameraSearch(manufacturer, model) {
    try {
      // Fire and forget - don't block UI for analytics
      fetch(`${this.apiUrl}/api/analytics/camera/${encodeURIComponent(manufacturer)}/${encodeURIComponent(model)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      }).catch(error => {
        // Silently fail - analytics shouldn't break user experience
        console.debug('Analytics tracking failed:', error);
      });
    } catch (error) {
      // Silently fail - analytics shouldn't break user experience
      console.debug('Analytics tracking error:', error);
    }
  }

  async onModelChange(modelKey) {
    if (!modelKey) {
      this.hideResults();
      return;
    }

    // INPUT VALIDATION: Verify model key exists in our cached data
    if (!this.cameraData[modelKey]) {
      console.error('Invalid model selection:', modelKey);
      this.showError();
      return;
    }

    this.showLoading();

    try {
      // Check if we have the data cached locally
      let cameraData = this.cameraData[modelKey];

      // If not, fetch from API
      if (!cameraData) {
        const response = await fetch(`${this.apiUrl}/api/camera/${encodeURIComponent(modelKey)}`);

        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }

        cameraData = await response.json();
        this.cameraData[modelKey] = cameraData;
      }

      // Track camera search analytics (async, non-blocking)
      this.trackCameraSearch(cameraData.manufacturer, cameraData.model);

      // Simulate slight delay for better UX
      setTimeout(() => {
        this.displayCompatibilityInfo(modelKey);
      }, 300);
    } catch (error) {
      console.error('Error loading camera details:', error);
      this.showError();
    }
  }

  displayCompatibilityInfo(modelKey) {
    const cameraData = this.cameraData[modelKey];

    if (!cameraData) {
      this.showError();
      return;
    }

    // Parse compatibility_status for badge
    let status = cameraData.compatibility_status;
    try {
      if (typeof status === 'string' && status.startsWith('[')) {
        const parsed = JSON.parse(status);
        status = Array.isArray(parsed) ? parsed[0] : status;
      }
    } catch (e) {
      console.warn('Could not parse compatibility_status:', status);
    }

    // Build camera info header with compatibility badge - SECURE METHOD
    // Clear existing content
    this.cameraInfo.innerHTML = '';

    // Create heading with safe text
    const heading = this.createSafeElement('h3', 'h3');
    this.setSafeText(heading, `${cameraData.manufacturer} ${cameraData.model}`);

    // Create badge with safe text
    const badgeClass = status.toLowerCase().replace(/ /g, '-');
    const badge = this.createSafeElement(
      'div',
      `camera-widget__compatibility-badge camera-widget__badge--${badgeClass}`
    );
    this.setSafeText(badge, status);

    // Append to DOM
    this.cameraInfo.appendChild(heading);
    this.cameraInfo.appendChild(badge);

    // Build result content - SECURE METHOD
    // Clear existing content
    this.cameraContent.innerHTML = '';

    // Compatibility status section with manufacturer-specific messaging
    const compatSection = this.buildCompatibilitySection(cameraData);
    this.cameraContent.appendChild(compatSection);

    // Last updated
    if (cameraData.last_updated) {
      const metaDiv = this.createSafeElement('div', 'camera-widget__meta');
      const small = this.createSafeElement('small');
      this.setSafeText(small, `Information last updated: ${this.formatDate(cameraData.last_updated)}`);
      metaDiv.appendChild(small);
      this.cameraContent.appendChild(metaDiv);
    }

    this.hideLoading();
    this.showResults();
  }

  getSectionIcon(type) {
    const icons = {
      'status': 'camera-widget__icon--status',
      'features': 'camera-widget__icon--features',
      'limitations': 'camera-widget__icon--limitations',
      'warnings': 'camera-widget__icon--warnings',
      'notes': 'camera-widget__icon--notes',
      'products': 'camera-widget__icon--products'
    };
    return icons[type] || '';
  }

  buildCompatibilitySection(cameraData) {
    // Parse compatibility_status - it comes as a string like '["Compatible"]'
    let status = cameraData.compatibility_status;
    try {
      if (typeof status === 'string' && status.startsWith('[')) {
        const parsed = JSON.parse(status);
        status = Array.isArray(parsed) ? parsed[0] : status;
      }
    } catch (e) {
      console.warn('Could not parse compatibility_status:', status);
    }

    const compatNotes = (cameraData.compatibility_notes && cameraData.compatibility_notes.trim() !== '') ? cameraData.compatibility_notes : '';
    const additionalNotes = (cameraData.additional_notes && cameraData.additional_notes.trim() !== '') ? cameraData.additional_notes : '';
    const isSony = cameraData.manufacturer && cameraData.manufacturer.toLowerCase() === 'sony';

    // Create container for all sections
    const container = document.createElement('div');

    // Build the main compatibility message based on status
    // compatibility_notes overrides the default status message if present
    let compatibilityMessage = '';
    let messageIcon = 'fa-circle-check';

    if (compatNotes) {
      // Custom compatibility notes provided - treat as safe text
      compatibilityMessage = compatNotes;
      messageIcon = 'fa-circle-info';
    } else {
      // Use predefined message based on status
      switch (status) {
        case 'Compatible':
          compatibilityMessage = 'This camera has been tested and confirmed to work seamlessly with the FJ Wireless System, supporting TTL, HSS, and full wireless control.';
          messageIcon = 'fa-circle-check';
          break;
        case 'Manual Mode Only':
          compatibilityMessage = 'This camera has been tested and confirmed to work with the FJ Wireless System in Manual Mode. While basic triggering functions perform reliably, full TTL and HSS compatibility cannot be guaranteed with this model.';
          messageIcon = 'fa-gear';
          break;
        case 'Not Yet Tested':
          compatibilityMessage = 'Westcott has not completed in-house testing for this model yet, though it shares many similarities with confirmed compatible versions.';
          messageIcon = 'fa-circle-info';
          break;
        case 'Not Compatible':
          compatibilityMessage = 'This camera is not currently compatible with the FJ Wireless System.';
          messageIcon = 'fa-circle-xmark';
          break;
        default:
          compatibilityMessage = 'Compatibility status unknown.';
          messageIcon = 'fa-circle-info';
      }
    }

    // Build the main message div with appropriate class - SECURE
    const cssClass = compatNotes ? 'custom' : (status.toLowerCase().replace(/ /g, '-'));
    const messageDiv = document.createElement('div');
    messageDiv.className = `camera-widget__compatibility-message camera-widget__compatibility-message--${cssClass}`;

    // Add icon
    const icon = document.createElement('i');
    icon.className = `fa-regular ${messageIcon}`;
    messageDiv.appendChild(icon);
    messageDiv.appendChild(document.createTextNode(' '));

    // Add message text in strong tag
    const strong = document.createElement('strong');
    this.setSafeText(strong, compatibilityMessage);
    messageDiv.appendChild(strong);

    // Build sections - compatibility results
    const compatSection = this.buildSectionElement('Compatibility Results', messageDiv, 'status');
    container.appendChild(compatSection);

    // Add Sony-specific section if applicable - SECURE
    if (isSony) {
      const sonyDiv = document.createElement('div');
      sonyDiv.className = 'camera-widget__sony-message';

      const sonyIcon = document.createElement('i');
      sonyIcon.className = 'fa-regular fa-camera';
      sonyDiv.appendChild(sonyIcon);
      sonyDiv.appendChild(document.createTextNode(' '));

      const sonyStrong = document.createElement('strong');
      this.setSafeText(sonyStrong, 'Important for Sony Users:');
      sonyDiv.appendChild(sonyStrong);

      sonyDiv.appendChild(document.createElement('br'));
      sonyDiv.appendChild(document.createTextNode('Choose the Sony version of triggers or speedlights or use the multi-brand version with the Sony Adapter. These options feature Sony\'s proprietary hot shoe mount for full TTL and sync functionality.'));

      const sonySection = this.buildSectionElement('Sony Camera Requirements', sonyDiv, 'notes');
      container.appendChild(sonySection);
    }

    // Add additional notes section if present - SECURE
    if (additionalNotes) {
      const notesDiv = document.createElement('div');
      notesDiv.className = 'camera-widget__additional-notes';

      const notesIcon = document.createElement('i');
      notesIcon.className = 'fa-regular fa-circle-info';
      notesDiv.appendChild(notesIcon);
      notesDiv.appendChild(document.createTextNode(' '));
      notesDiv.appendChild(document.createTextNode(additionalNotes));

      const notesSection = this.buildSectionElement('Additional Notes', notesDiv, 'warnings');
      container.appendChild(notesSection);
    }

    return container;
  }

  /**
   * Build a section element safely (replaces buildSection)
   * @param {string} title - Section title
   * @param {HTMLElement} contentElement - Content element to append
   * @param {string} type - Section type for CSS class
   * @returns {HTMLElement} Section container
   */
  buildSectionElement(title, contentElement, type) {
    const container = document.createElement('div');

    // Title
    const titleElement = this.createSafeElement('h4', 'camera-widget__section-title', title);
    container.appendChild(titleElement);

    // Content wrapper
    const contentWrapper = document.createElement('div');
    contentWrapper.className = `camera-widget__section camera-widget__section--${type}`;
    contentWrapper.appendChild(contentElement);
    container.appendChild(contentWrapper);

    return container;
  }

  getCompatibilityDescription(status) {
    // This method is now handled by buildCompatibilitySection
    return status;
  }

  formatDate(dateStr) {
    try {
      // Display date exactly as stored, without timezone conversion
      // Expected format: YYYY-MM-DD or M/D/YYYY
      if (!dateStr) return '';

      // If already in M/D/YYYY format, return as-is
      if (dateStr.includes('/')) {
        return dateStr;
      }

      // If in YYYY-MM-DD format, convert to M/D/YYYY without timezone shift
      if (dateStr.includes('-')) {
        const [year, month, day] = dateStr.split('-');
        return `${parseInt(month)}/${parseInt(day)}/${year}`;
      }

      return dateStr;
    } catch (e) {
      return dateStr;
    }
  }

  showStep(stepName) {
    const step = document.getElementById(`step-${stepName}`);
    if (step) {
      step.style.display = 'block';
      step.classList.add('camera-widget__step--active');
    }
  }

  hideStep(stepName) {
    const step = document.getElementById(`step-${stepName}`);
    if (step) {
      step.style.display = 'none';
      step.classList.remove('camera-widget__step--active');
    }
  }

  showResults() {
    // Show results with smooth expansion
    this.resultsContainer.classList.add('camera-widget__results--visible');
    this.errorMessage.style.display = 'none';

    // Collapse form to nothing
    if (this.cameraForm) {
      this.cameraForm.classList.add('camera-widget__form--collapsed');
    }
  }

  hideResults() {
    // Hide results with smooth collapse
    this.resultsContainer.classList.remove('camera-widget__results--visible');
  }

  showForm() {
    // Show the form and hide results (for "Change Camera" button)

    // Collapse results to nothing
    this.resultsContainer.classList.remove('camera-widget__results--visible');

    // Expand form back
    if (this.cameraForm) {
      this.cameraForm.classList.remove('camera-widget__form--collapsed');
    }
  }

  showLoading() {
    this.loadingIndicator.style.display = 'block';
    this.hideResults();
    this.errorMessage.style.display = 'none';
  }

  hideLoading() {
    this.loadingIndicator.style.display = 'none';
  }

  showError() {
    this.hideLoading();
    this.hideResults();
    this.errorMessage.style.display = 'block';
  }

  resetWidget() {
    // Reset form
    this.manufacturerSelect.value = '';
    this.modelSelect.value = '';
    this.modelSelect.innerHTML = '<option value="">Choose model...</option>';

    // Reset display
    this.hideStep('model');
    this.hideResults();
    this.hideLoading();
    this.errorMessage.style.display = 'none';

    // Show form (expand if collapsed)
    if (this.cameraForm) {
      this.cameraForm.classList.remove('camera-widget__form--collapsed');
    }

    // Focus on first step
    this.manufacturerSelect.focus();
  }
}

// Initialize widget when DOM is loaded
document.addEventListener('DOMContentLoaded', function () {
  // Initialize widget with API
  new CameraCompatibilityWidget();
});

// Export for potential external use
window.CameraCompatibilityWidget = CameraCompatibilityWidget;